import React, { useState, useEffect, useMemo } from 'react';
import { ClipboardList, Calendar, Package } from 'lucide-react';
import { Order, OrderStatus, Product } from '../../../types';
import { formatPhoneNumber } from '../../../utils/formatters';
import { parseStoreOrderRequestText } from '../../../utils/storePickup';
import {
  getOrderItemAggregationKey,
  getOrderItemDisplayName,
} from '../../../utils/orderItemDisplay';


interface OrderListViewProps {
  orders: Order[];
  totalCount?: number;
  currentPage?: number;
  totalPages?: number;
  statusFilter?: 'ALL' | OrderStatus;
  onStatusFilterChange?: (status: 'ALL' | OrderStatus) => void;
  statusCounts?: {
    ALL: number;
    PENDING: number;
    READY: number;
    COMPLETED: number;
    CANCELLED?: number;
  };
  onUpdateOrderStatus?: (order: Order) => void;
  onUpdateOrderStatusBatch?: (
    ids: string[],
    status: OrderStatus,
    modifierRole?: string,
    partialUpdates?: { id: string; itemIds: string[] }[],
  ) => Promise<void>;
  customerAliases?: Record<string, string>;
  onOpenAdminOrder?: (
    name?: string,
    phone?: string,
    order?: Order,
    storeId?: string,
    orderSheetId?: string,
    userId?: string
  ) => void;
  onViewHistory?: (name: string, phone: string, userId?: string, orderSheetId?: string) => void;
  sortBy?: 'default' | 'name';
  products: Product[];
  onPageChange?: (page: number) => void;
  onFetchAllOrdersForSelection?: () => Promise<Order[]>;
  selectionScopeKey?: string;
}

export const OrderListView = ({
  orders,
  totalCount = 0,
  currentPage = 1,
  totalPages = 0,
  statusFilter = 'ALL',
  onStatusFilterChange,
  statusCounts,
  onUpdateOrderStatus,
  onUpdateOrderStatusBatch,
  customerAliases,
  onOpenAdminOrder,
  onViewHistory,
  sortBy,
  products,
  onPageChange,
  onFetchAllOrdersForSelection,
  selectionScopeKey,
}: OrderListViewProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([]);
  const [batchStatus, setBatchStatus] = useState<OrderStatus>('READY');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  useEffect(() => {
    setSelectedIds([]);
    setSelectedGroupKeys([]);
  }, [selectionScopeKey, statusFilter, sortBy]);

  const sortedOrders = useMemo(() => {
    if (!orders) return [];

    const hasServerGroupedOrders = orders.some(
      (order) => Array.isArray(order.aggregatedIds) && order.aggregatedIds.length > 0,
    );

    const displayOrders = hasServerGroupedOrders
      ? orders.map((order) => ({
        ...order,
        aggregatedIds:
          order.aggregatedIds && order.aggregatedIds.length > 0
            ? order.aggregatedIds
            : [order.id],
      }))
      : (() => {
        const map: Record<string, any> = {};

        orders.forEach((order: any) => {
          const phoneVal = (order.customerPhone || '').replace(/\D/g, '');
          const sheetId = order.orderSheetId || order.storeId || '';
          const name = (order.customerName || '').trim();
          const key = `${sheetId}-${name}-${phoneVal}`;

          if (!map[key]) {
            map[key] = {
              ...order,
              items: [...(order.items || [])],
              aggregatedIds: [order.id],
            };
            return;
          }

          map[key].items = [...map[key].items, ...(order.items || [])];
          map[key].totalPrice =
            (map[key].totalPrice || 0) + (order.totalPrice || 0);
          map[key].aggregatedIds.push(order.id);

          if (order.request && !map[key].request) {
            map[key].request = order.request;
          } else if (
            order.request &&
            map[key].request &&
            !map[key].request.includes(order.request)
          ) {
            map[key].request += `\n${order.request}`;
          }

          if (order.sellerMemo && !map[key].sellerMemo) {
            map[key].sellerMemo = order.sellerMemo;
          } else if (
            order.sellerMemo &&
            map[key].sellerMemo &&
            !map[key].sellerMemo.includes(order.sellerMemo)
          ) {
            map[key].sellerMemo += `\n${order.sellerMemo}`;
          }
        });

        return Object.values(map).map((order) => {
          const itemMap: Record<string, any> = {};

          order.items.forEach((item: any) => {
            const pId = getOrderItemAggregationKey(item);

            if (!itemMap[pId]) {
              itemMap[pId] = { ...item };
            } else {
              itemMap[pId].quantity += item.quantity;
            }
          });

          return { ...order, items: Object.values(itemMap) };
        });
      })();

    return displayOrders;
  }, [orders]);

  const currentPageGroupedOrderCount = sortedOrders.length;
  const totalSelectableOrderCount = totalCount || currentPageGroupedOrderCount;
  const selectedGroupedOrderCount = selectedGroupKeys.length;
  const isAllFilteredSelected =
    totalSelectableOrderCount > 0 &&
    selectedGroupedOrderCount >= totalSelectableOrderCount;

  const getSelectionIds = (order: any) =>
    order.aggregatedIds && order.aggregatedIds.length > 0
      ? order.aggregatedIds
      : [order.id];

  const getSelectionKey = (order: any) => order.id;

  const isOrderSelected = (order: any) => {
    const ids = getSelectionIds(order);

    return ids.length > 0 && ids.every((id: string) => selectedIds.includes(id));
  };

  const filters: { key: 'ALL' | OrderStatus; label: string; activeColor: string; badgeColor: string }[] = [
    { key: 'ALL', label: '전체', activeColor: 'bg-gray-900 text-white border-gray-900', badgeColor: 'bg-gray-700 text-white' },
    { key: 'PENDING', label: '주문완료', activeColor: 'bg-blue-600 text-white border-blue-600', badgeColor: 'bg-blue-400 text-white' },
    { key: 'READY', label: '상품준비완료', activeColor: 'bg-amber-500 text-white border-amber-500', badgeColor: 'bg-amber-300 text-white' },
    { key: 'COMPLETED', label: '입금/수령완료', activeColor: 'bg-emerald-600 text-white border-emerald-600', badgeColor: 'bg-emerald-400 text-white' },
  ];

  const filterBar = (
    <div className="flex gap-2 flex-wrap">
      {filters.map((filter) => {
        const count = statusCounts?.[filter.key] ?? 0;
        const isActive = statusFilter === filter.key;
        return (
          <button
            key={filter.key}
            onClick={() => onStatusFilterChange?.(filter.key)}
            className={`flex items-center gap-1.5 px-4 h-10 rounded-2xl border font-black transition-all text-xs whitespace-nowrap shadow-sm ${isActive ? filter.activeColor : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}
          >
            {filter.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${isActive ? filter.badgeColor : 'bg-gray-100 text-gray-500'}`}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );

  const getOrderRequestDisplay = (request?: string) => {
    const rawRequest = request?.trim() || '';
    const { pickupLabel, requestMessage } =
      parseStoreOrderRequestText(rawRequest);

    return {
      pickupLabel,
      requestMessage: requestMessage || (!pickupLabel ? rawRequest : ''),
    };
  };

  if (!orders || orders.length === 0) {
    return (
      <div className="space-y-4">
        {filterBar}
        <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
          <ClipboardList size={48} strokeWidth={1} />
          <p className="text-sm font-bold">
            {statusFilter === 'ALL' ? '주문 내역이 없습니다.' : '선택한 상태의 주문이 없습니다.'}
          </p>
        </div>
      </div>
    );
  }

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedGroupKeys([]);
  };

  const toggleVisibleSelection = () => {
    const visibleIds = sortedOrders.flatMap((o: any) => getSelectionIds(o));
    const visibleGroupKeys = sortedOrders.map((o: any) => getSelectionKey(o));
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      setSelectedGroupKeys((prev) =>
        prev.filter((key) => !visibleGroupKeys.includes(key)),
      );
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
      setSelectedGroupKeys((prev) =>
        Array.from(new Set([...prev, ...visibleGroupKeys])),
      );
    }
  };

  const toggleSelect = (order: any) => {
    const idsToToggle = getSelectionIds(order);
    const groupKey = getSelectionKey(order);
    const allSelected = idsToToggle.every((id: string) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(i => !idsToToggle.includes(i)));
      setSelectedGroupKeys(prev => prev.filter(key => key !== groupKey));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...idsToToggle])));
      setSelectedGroupKeys(prev => Array.from(new Set([...prev, groupKey])));
    }
  };

  const toggleAllOrderSelection = async () => {
    if (isAllFilteredSelected) {
      clearSelection();
      return;
    }

    if (!onFetchAllOrdersForSelection) {
      toggleVisibleSelection();
      return;
    }

    setIsSelectingAll(true);
    try {
      const allOrders = await onFetchAllOrdersForSelection();
      setSelectedIds(
        Array.from(
          new Set(allOrders.flatMap((order: any) => getSelectionIds(order))),
        ),
      );
      setSelectedGroupKeys(
        Array.from(
          new Set(allOrders.map((order: any) => getSelectionKey(order))),
        ),
      );
    } catch (error) {
      console.error('Failed to select all orders', error);
      alert('전체 주문 내역을 선택하지 못했습니다.');
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedIds.length === 0) {
      alert('선택된 주문이 없습니다.');
      return;
    }
    if (!onUpdateOrderStatusBatch) return;

    if (confirm(`${selectedGroupedOrderCount}건의 주문 상태를 변경하시겠습니까?`)) {
      setIsUpdating(true);
      try {
        await onUpdateOrderStatusBatch(selectedIds, batchStatus);
        clearSelection();
        alert('변경되었습니다.');
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleUpdateAllInSheet = async () => {
    if (!onUpdateOrderStatusBatch) return;
    const allIds = sortedOrders.flatMap((o: any) => o.aggregatedIds || [o.id]);
    if (confirm(`현재 목록의 모든 주문(${currentPageGroupedOrderCount}건) 상태를 변경하시겠습니까?`)) {
      setIsUpdating(true);
      try {
        await onUpdateOrderStatusBatch(allIds, batchStatus);
        clearSelection();
        alert('변경되었습니다.');
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      {filterBar}
      {/* Batch Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl border shadow-sm">
        <div className="flex items-center gap-3">
          <span className="text-xs font-black text-gray-900">
            선택된 주문건수: <span className="text-emerald-500">{selectedGroupedOrderCount}</span> / {totalSelectableOrderCount}
          </span>
          <button
            onClick={toggleAllOrderSelection}
            disabled={isSelectingAll || totalSelectableOrderCount === 0}
            className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 underline underline-offset-2 disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
          >
            {isSelectingAll
              ? '전체 주문 내역 선택 중...'
              : isAllFilteredSelected
                ? '전체 주문 내역 선택 해제'
                : '전체 주문 내역 선택'}
          </button>
          <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-black text-gray-500">
            전체 {totalSelectableOrderCount}건
          </span>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={batchStatus}
            onChange={(e) => setBatchStatus(e.target.value as OrderStatus)}
            className="px-3 py-2 rounded-xl text-[11px] font-black border border-gray-200 outline-none cursor-pointer"
          >
            <option value="PENDING">주문완료</option>
            <option value="READY">상품준비완료</option>
            <option value="COMPLETED">입금/수령완료</option>
          </select>
          <button
            onClick={handleBatchUpdate}
            disabled={selectedIds.length === 0 || isUpdating}
            className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-[11px] font-black hover:bg-emerald-600 transition-colors disabled:bg-gray-200 disabled:cursor-not-allowed"
          >
            선택 항목 변경
          </button>
          <button
            onClick={handleUpdateAllInSheet}
            disabled={isUpdating}
            className="px-4 py-2 bg-gray-900 text-white rounded-xl text-[11px] font-black hover:bg-gray-800 transition-colors disabled:bg-gray-200 disabled:cursor-not-allowed"
          >
            현재 목록 전체 변경
          </button>
        </div>
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-[32px] border shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-6 py-4 w-12 text-center">
                <input
                  type="checkbox"
                  checked={sortedOrders.length > 0 && sortedOrders.every(order => isOrderSelected(order))}
                  onChange={toggleVisibleSelection}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter w-40">주문 정보</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">고객 정보</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">주문 품목</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right">총 주문 금액</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-center w-40">진행 상태</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-center w-32">관리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sortedOrders.map(order => {
              const mainItem = order.items && order.items.length > 0 ? order.items[0] : null;
              const otherCount = order.items ? order.items.length - 1 : 0;
              const requestDisplay = getOrderRequestDisplay(order.request);
              const sellerMemo = order.sellerMemo?.trim();
              const displayName = customerAliases && order.customerPhone && customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
                ? `${customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]} (${order.customerName})`
                : order.customerName;

              return (
                <tr key={order.id} className={`hover:bg-gray-50/50 transition-colors group ${isOrderSelected(order) ? 'bg-emerald-50/30' : ''}`}>
                  <td className="px-6 py-4 w-12 text-center">
                    <input
                      type="checkbox"
                      checked={isOrderSelected(order)}
                      onChange={() => toggleSelect(order)}
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                        <Calendar size={10} /> {order.date}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs font-black text-gray-900">
                        <Package size={10} className="text-emerald-500" /> {order.storeName}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => onViewHistory?.(order.customerName, order.customerPhone || '', order.userId, order.orderSheetId)}
                        className="text-sm font-black text-gray-900 hover:text-emerald-600 transition-colors text-left"
                      >
                        {displayName}
                      </button>
                      <span className="text-[10px] text-gray-400 font-bold">{formatPhoneNumber(order.customerPhone)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        {mainItem && (
                          <>
                            <div className="w-10 h-10 rounded-lg border bg-gray-50 overflow-hidden shrink-0 shadow-inner">
                              {mainItem.image ? (
                                <img src={mainItem.image} className="w-full h-full object-cover" alt="" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                  <Package size={14} />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-black truncate">{getOrderItemDisplayName(mainItem)}</p>
                              {otherCount > 0 && (
                                <p className="text-[10px] font-bold text-emerald-500">외 {otherCount}건</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {(requestDisplay.pickupLabel || requestDisplay.requestMessage) && (
                        <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2">
                          <p className="text-[10px] font-black text-amber-600 uppercase tracking-tighter">
                            고객 요청사항
                          </p>
                          {requestDisplay.pickupLabel && (
                            <p className="mt-1 text-[10px] font-bold text-gray-500">
                              방문 시간: <span className="text-gray-700">{requestDisplay.pickupLabel}</span>
                            </p>
                          )}
                          {requestDisplay.requestMessage && (
                            <p className="mt-1 text-[11px] font-bold text-gray-700 whitespace-pre-wrap">
                              {requestDisplay.requestMessage}
                            </p>
                          )}
                        </div>
                      )}
                      {sellerMemo && (
                        <div className="rounded-xl bg-sky-50 border border-sky-100 px-3 py-2">
                          <p className="text-[10px] font-black text-sky-600 uppercase tracking-tighter">
                            판매자 메모
                          </p>
                          <p className="mt-1 text-[11px] font-bold text-gray-700 whitespace-pre-wrap">
                            {sellerMemo}
                          </p>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-black text-gray-900">{order.totalPrice.toLocaleString()}원</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {onUpdateOrderStatus && (
                        <select
                          value={order.status}
                          onChange={(e) => {
                            if (onUpdateOrderStatusBatch && order.aggregatedIds && order.aggregatedIds.length > 1) {
                              onUpdateOrderStatusBatch(order.aggregatedIds, e.target.value as OrderStatus);
                            } else {
                              onUpdateOrderStatus({ ...order, status: e.target.value as OrderStatus });
                            }
                          }}
                          className={`w-full max-w-[140px] px-3 py-2 rounded-xl text-[10px] font-black border transition-all outline-none cursor-pointer appearance-none text-center ${order.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                            order.status === 'CANCELLED' ? 'bg-red-50 text-red-500 border-red-200' :
                              order.status === 'READY' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                'bg-blue-50 text-blue-600 border-blue-200'
                            }`}
                        >
                          <option value="PENDING">주문완료</option>
                          <option value="READY">상품준비완료</option>
                          <option value="COMPLETED">입금/수령완료</option>
                        </select>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {onOpenAdminOrder && (
                        <button
                          onClick={() => onOpenAdminOrder?.('', '', order)}
                          className="px-3 py-2 bg-gray-50 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl text-[10px] font-black transition-all border border-transparent hover:border-gray-200"
                        >
                          주문 수정
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4 print:hidden">
        {sortedOrders.map(order => {
          const requestDisplay = getOrderRequestDisplay(order.request);
          const sellerMemo = order.sellerMemo?.trim();
          const displayName = customerAliases && order.customerPhone && customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
            ? `${customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]} (${order.customerName})`
            : order.customerName;

          return (
            <div key={order.id} className={`bg-white rounded-[28px] border shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow ${isOrderSelected(order) ? 'bg-emerald-50/50 border-emerald-200' : ''}`}>
              <div className="px-5 py-4 bg-gray-50/50 flex justify-between items-center border-b">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isOrderSelected(order)}
                    onChange={() => toggleSelect(order)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => onViewHistory?.(order.customerName, order.customerPhone || '', order.userId, order.orderSheetId)}
                      className="text-sm font-black text-gray-900 hover:text-emerald-600 transition-colors text-left"
                    >
                      {displayName}
                    </button>
                    <div className="flex items-center gap-1.5 text-[9px] text-gray-400 font-bold uppercase tracking-tight">
                      <Calendar size={10} /> {order.date}
                      <span className="text-gray-200">|</span>
                      <Package size={10} /> {order.storeName}
                    </div>
                  </div>
                </div>
                {onUpdateOrderStatus && (
                  <select
                    value={order.status}
                    onChange={(e) => {
                      if (onUpdateOrderStatusBatch && order.aggregatedIds && order.aggregatedIds.length > 1) {
                        onUpdateOrderStatusBatch(order.aggregatedIds, e.target.value as OrderStatus);
                      } else {
                        onUpdateOrderStatus({ ...order, status: e.target.value as OrderStatus });
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black border transition-colors outline-none cursor-pointer appearance-none ${order.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                      order.status === 'CANCELLED' ? 'bg-red-50 text-red-500 border-red-200' :
                        order.status === 'READY' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                          'bg-blue-50 text-blue-600 border-blue-200'
                      }`}
                  >
                    <option value="PENDING">주문완료</option>
                    <option value="READY">상품준비완료</option>
                    <option value="COMPLETED">입금/수령완료</option>
                  </select>
                )}
              </div>

              <div className="p-5 space-y-4">
                <div className="space-y-3">
                  {(order.items || []).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg border bg-gray-50 overflow-hidden shrink-0">
                        {item.image ? (
                          <img src={item.image} className="w-full h-full object-cover" alt="" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <Package size={14} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-black truncate leading-tight mb-0.5">{getOrderItemDisplayName(item)}</p>
                        <p className="text-[10px] font-bold text-gray-400 leading-none">{item.quantity}개 / {item.price.toLocaleString()}원</p>
                      </div>
                      <div className="text-[11px] font-black text-gray-900">{(item.price * item.quantity).toLocaleString()}원</div>
                    </div>
                  ))}
                </div>
                {(requestDisplay.pickupLabel || requestDisplay.requestMessage) && (
                  <div className="rounded-2xl bg-amber-50 border border-amber-100 px-3 py-3">
                    <p className="text-[10px] font-black text-amber-600 uppercase tracking-tighter">
                      고객 요청사항
                    </p>
                    {requestDisplay.pickupLabel && (
                      <p className="mt-1 text-[10px] font-bold text-gray-500">
                        방문 시간: <span className="text-gray-700">{requestDisplay.pickupLabel}</span>
                      </p>
                    )}
                    {requestDisplay.requestMessage && (
                      <p className="mt-1 text-[11px] font-bold text-gray-700 whitespace-pre-wrap">
                        {requestDisplay.requestMessage}
                      </p>
                    )}
                  </div>
                )}
                {sellerMemo && (
                  <div className="rounded-2xl bg-sky-50 border border-sky-100 px-3 py-3">
                    <p className="text-[10px] font-black text-sky-600 uppercase tracking-tighter">
                      판매자 메모
                    </p>
                    <p className="mt-1 text-[11px] font-bold text-gray-700 whitespace-pre-wrap">
                      {sellerMemo}
                    </p>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 bg-gray-50/30 border-t flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tight leading-none mb-1">총 주문 금액</span>
                  <span className="text-base font-black text-gray-900">{order.totalPrice.toLocaleString()}원</span>
                </div>
                {onOpenAdminOrder && (
                  <button
                    onClick={() => onOpenAdminOrder?.('', '', order)}
                    className="px-3 py-1.5 bg-white border text-[10px] font-black text-gray-500 hover:text-gray-900 rounded-lg transition-colors"
                  >
                    주문 수정
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(totalPages > 1 || sortedOrders.length > 0) && (
        <div className="flex flex-col items-center gap-3 pt-2 pb-6 print:hidden">
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2">
              <button
                onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                이전
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 2,
                  )
                  .map((page, index, arr) => (
                    <React.Fragment key={page}>
                      {index > 0 && arr[index - 1] !== page - 1 ? (
                        <span className="text-gray-400 text-xs px-1">...</span>
                      ) : null}
                      <button
                        onClick={() => onPageChange?.(page)}
                        className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-colors ${currentPage === page
                            ? 'bg-gray-900 text-white shadow-sm'
                            : 'bg-white border text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() =>
                  onPageChange?.(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                다음
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
